import { ref, createApp } from "vue";
import type { App } from "vue";
import ToastContainer from "~/ui-kit/components/toast/toastContainer.vue";
import { v4 as uuidv4 } from "uuid";

interface ToastObject extends ToastOptions {
	toastId: string;
	index: number;
	isShown: boolean;
}

interface ToastOptions {
	message: string;
	description: string;
	type?: string;
	dialog?: boolean;
	onConfirm?: () => void;
	onCancel?: () => void;
	confirmText?: string;
	cancelText?: string;
}

// Default options
const toastOptions: ToastOptions = {
	message: "",
	description: "",
	type: "info",
	dialog: false,
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onConfirm: () => {},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onCancel: () => {},
	confirmText: "Confirm",
	cancelText: "Cancel",
};

export const toastsList = ref<ToastObject[]>([]);
const toastRootContainerId = "toastRootContainer";

let currentIndex = 0;
const maxToasts = 6;

const toastRootContainer = document.createElement("div");
toastRootContainer.setAttribute("id", toastRootContainerId);

let toastContainerApp: App<Element>;

function validateOptions(options: ToastOptions) {
	if (options.message) toastOptions.message = options.message;

	if (options.description) toastOptions.description = options.description;

	if (options.type) toastOptions.type = options.type;

	if (options.dialog !== undefined) toastOptions.dialog = options.dialog;

	if (typeof options.onCancel === "function" && options.onCancel instanceof Function)
		toastOptions.onCancel = options.onCancel;

	if (typeof options.onConfirm === "function" && options.onConfirm instanceof Function)
		toastOptions.onConfirm = options.onConfirm;

	if (options.confirmText) toastOptions.confirmText = options.confirmText;

	if (options.cancelText) toastOptions.cancelText = options.cancelText;
}

function hideToastById(toastId: string) {
	toastsList.value.map((object) => {
		if (object.toastId === toastId) object.isShown = false;
	});
}

function restoreToastOptions() {
	toastOptions.message = "";
	toastOptions.description = "";
	toastOptions.type = "info";
	toastOptions.dialog = false;
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	toastOptions.onConfirm = () => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	toastOptions.onCancel = () => {};
	toastOptions.confirmText = "Confirm";
	toastOptions.cancelText = "Cancel";
}

export function showToast(options: ToastOptions) {
	validateOptions(options);

	// Add element to toasts list if its not containts dialog toast and toasts list length is not greater then maxToasts
	if (toastsList.value.filter((object) => object.dialog).length === 0 && toastsList.value.length <= maxToasts - 1) {
		toastsList.value.push({
			toastId: uuidv4(),
			index: currentIndex++,
			isShown: true,
			message: toastOptions.message,
			description: toastOptions.description,
			type: toastOptions.type,
			dialog: toastOptions.dialog,
			onConfirm: toastOptions.onConfirm,
			onCancel: toastOptions.onCancel,
			confirmText: toastOptions.confirmText,
			cancelText: toastOptions.cancelText,
		});
	}

	restoreToastOptions();

	// Create toast app and mount into the DOM
	if (!document.querySelector(`#${toastRootContainerId}`)) {
		document.body.appendChild(toastRootContainer);
		toastContainerApp = createApp(ToastContainer);
		toastContainerApp.mount(toastRootContainer);
	}
}

export function hideToast(toastId: string) {
	hideToastById(toastId);

	if (toastsList.value.filter((object) => object.isShown).length === 0) {
		toastsList.value = [];
		toastContainerApp.unmount();
		toastRootContainer.remove();
	}
}
